import { PublicSettings } from "@/src/config/PublicSettings";
import type { RumInitConfiguration, RumErrorEvent } from "@datadog/browser-rum";

import React from "react";
import Script from "next/script";

const toNumber = (value: string | number | undefined): number | undefined => {
    if (value === undefined) return undefined;
    const num = typeof value === "string" ? parseFloat(value) : value;
    return isNaN(num) ? undefined : num;
};

const initializeDatadog = (config: RumInitConfiguration) => {
    const maxAttempts = 50;
    let attempts = 0;

    const tryInit = () => {
        if (window.DD_RUM) {
            window.DD_RUM.onReady(() => {
                window.DD_RUM?.init(config);
            });
        } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(tryInit, 100);
        }
    };

    tryInit();
};

export const DataDog: React.FC<{ replaySampleRate?: number }> = ({ replaySampleRate }) => {
    if (!PublicSettings.DATADOG_CLIENT_TOKEN || !PublicSettings.DATADOG_APPLICATION_ID) {
        return null;
    }

    const effectiveReplayRate = replaySampleRate ?? toNumber(PublicSettings.DATADOG_SESSION_RECORD_RATE) ?? 0;

    const config: RumInitConfiguration = {
        clientToken: PublicSettings.DATADOG_CLIENT_TOKEN,
        applicationId: PublicSettings.DATADOG_APPLICATION_ID,
        site: "datadoghq.com",
        service: PublicSettings.DATADOG_SITE_NAME,
        env: PublicSettings.ENVIRONMENT,
        version: PublicSettings.DATADOG_APPLICATION_VERSION ?? "unknown",
        sessionSampleRate: toNumber(PublicSettings.DATADOG_SESSION_SAMPLE_RATE) ?? 0,
        sessionReplaySampleRate: effectiveReplayRate,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask-user-input",
        trackSessionAcrossSubdomains: true,
        beforeSend: event => {
            if (event.error) {
                const error = (event as RumErrorEvent).error;
                const regex = /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;
                if (regex.test(error.message)) return false;
            }
            return true;
        }
    };

    return (
        <>
            <Script
                id="datadog-loader"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `(function(h,o,u,n,d) {
    h=h[d]=h[d]||{q:[],onReady:function(c){h.q.push(c)}}
    d=o.createElement(u);d.async=1;d.src=n
    n=o.getElementsByTagName(u)[0];n.parentNode.insertBefore(d,n)
  })(window,document,'script','https://www.datadoghq-browser-agent.com/us1/v5/datadog-rum.js','DD_RUM')`
                }}
            />
            <Script
                id="datadog-init"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `(${initializeDatadog.toString()})(${JSON.stringify(config)})`
                }}
            />
        </>
    );
};
